/* Editable bag, shared by the /cart page and the header bag drawer.
   Markup: snippets/cart-body.liquid ([data-cart-body]). Edits go through Shopify's Ajax cart
   API (/cart/change.js), then Shopify re-renders the body (/cart?view=drawer), so line prices,
   discounts and totals always come from Shopify, never from this script.
   - qty -/+ buttons, typed qty (debounced), Remove; edits run one at a time, in order.
   - Focus stays on the control you used (or the next line if yours was removed).
   - Updates are announced in a polite live region ([data-cart-live]).
   - Fires "sayso:cart-changed" on document with { cart } so the hoodie builder and the
     header bag count stay in sync.
   Public: window.saysoCartDrawer.open(trigger?) / .refresh() / .setCount(n) (the one bag-count updater). */
(() => {
  if (window.saysoCartDrawer) return;

  // Locale-aware routes from theme.liquid (window.saysoRoutes), same source as script.js.
  const ROUTES = window.saysoRoutes || {};
  const ROOT = window.Shopify?.routes?.root || "/";
  const CART_URL = ROUTES.cart || `${ROOT}cart`;
  const BODY_URL = `${CART_URL}?view=drawer`;
  const CHANGE_URL = ROUTES.cartChange ? `${ROUTES.cartChange}.js` : `${ROOT}cart/change.js`;
  const TYPE_DELAY = 500;
  const drawer = document.getElementById("cart-drawer");
  const drawerContent = drawer?.querySelector("[data-cart-drawer-content]");

  const announce = (scope, message) => {
    const live = scope?.closest("#cart-drawer, .cart-inner")?.querySelector("[data-cart-live]") ||
      document.querySelector("[data-cart-live]");
    if (!live) return;
    live.textContent = "";
    window.setTimeout(() => { live.textContent = message; }, 60); // re-announce identical text
  };

  // The one bag-count updater: the bag drawer here and the hoodie builder (script.js, via
  // window.saysoCartDrawer.setCount after its flying "1" lands) both use it.
  const setBagCount = (count) => {
    const n = Math.max(0, Number(count || 0));
    document.querySelectorAll(".bag-link").forEach((link) => {
      let badge = link.querySelector("[data-cart-count], .bag-count");
      if (n <= 0) { badge?.remove(); return; }
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "bag-count";
        link.append(badge);
      }
      badge.dataset.cartCount = "true";
      badge.textContent = String(n);
    });
  };

  // ---- fetch + swap the Shopify-rendered body --------------------------------------------
  let prefetched = null; // { at, promise }
  const fetchBody = () =>
    fetch(BODY_URL, { headers: { Accept: "text/html" }, credentials: "same-origin", cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`bag ${res.status}`);
        return res.text();
      })
      .then((html) => new DOMParser().parseFromString(html, "text/html").querySelector("[data-cart-body]"));

  const freshBody = () => {
    if (prefetched && Date.now() - prefetched.at < 5000) {
      const { promise } = prefetched;
      prefetched = null;
      return promise;
    }
    return fetchBody();
  };

  const prefetch = () => {
    if (!prefetched || Date.now() - prefetched.at > 5000) {
      prefetched = { at: Date.now(), promise: fetchBody().catch(() => null) };
    }
  };

  const controlKey = (el) => {
    const line = el?.closest?.("[data-cart-line-key]");
    const control = el?.closest?.("[data-cart-control]")?.dataset.cartControl;
    return line && control ? { key: line.dataset.cartLineKey, control } : null;
  };

  const swapBody = (container, next, focusWas = null) => {
    if (!container || !next) return null;
    const old = container.querySelector("[data-cart-body]");
    const lineKeys = old ? Array.from(old.querySelectorAll("[data-cart-line-key]"), (l) => l.dataset.cartLineKey) : [];
    if (old) old.replaceWith(next);
    else container.replaceChildren(next);
    container.removeAttribute("aria-busy");
    if (focusWas) {
      const sel = (key) => `[data-cart-line-key="${CSS.escape(key)}"]`;
      const same = next.querySelector(`${sel(focusWas.key)} [data-cart-control="${focusWas.control}"]`);
      if (same) same.focus({ preventScroll: true });
      else {
        // Line removed: go to the next line that is still there, else the drawer heading / total.
        const from = lineKeys.indexOf(focusWas.key);
        const nextKey = lineKeys.slice(from + 1).concat(lineKeys.slice(0, from).reverse())
          .find((k) => next.querySelector(sel(k)));
        const target = (nextKey && next.querySelector(`${sel(nextKey)} [data-cart-control="dec"]`)) ||
          container.closest("#cart-drawer")?.querySelector("[data-panel-autofocus]") ||
          next.querySelector(".add-button, .cart-checkout-button");
        target?.focus({ preventScroll: true });
      }
    }
    return next;
  };

  const refresh = async (container = drawerContent) => {
    if (!container) return;
    try {
      const body = (await freshBody()) || (await fetchBody());
      if (!body) throw new Error("bag body missing"); // show the fallback, not an endless "Loading…"
      swapBody(container, body);
    } catch {
      if (!container.querySelector("[data-cart-body]")) {
        container.innerHTML = `<p class="cart-drawer-loading">Couldn't load your bag. <a href="${CART_URL}">Open the bag page</a>.</p>`;
      }
    }
  };

  // ---- edits ---------------------------------------------------------------------------
  let queue = Promise.resolve();
  const setBusy = (body, busy) => {
    if (!body) return;
    body.classList.toggle("is-cart-autoupdating", busy);
    body.setAttribute("aria-busy", String(busy));
    body.querySelectorAll(".cart-checkout-button").forEach((b) => { b.disabled = busy; });
  };

  const change = (body, key, quantity) => {
    queue = queue.then(async () => {
      const container = body.isConnected ? body.parentElement : null;
      const liveBody = container?.querySelector("[data-cart-body]") || body;
      const focusWas = controlKey(document.activeElement) || { key, control: quantity === 0 ? "remove" : "qty" };
      setBusy(liveBody, true);
      try {
        const res = await fetch(CHANGE_URL, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity }),
        });
        const cart = await res.json().catch(() => null);
        if (!res.ok || !cart || cart.status) throw new Error(cart?.description || cart?.message || "Couldn't update your bag. Please try again.");
        setBagCount(cart.item_count);
        document.dispatchEvent(new CustomEvent("sayso:cart-changed", { detail: { cart } }));
        const next = swapBody(container, await fetchBody(), focusWas);
        const total = next?.querySelector("[data-cart-total]")?.textContent.trim();
        const what = quantity === 0 ? "Removed from your bag." : "Bag updated.";
        announce(container, cart.item_count ? `${what} ${cart.item_count} in bag, total ${total || ""}`.trim() : "Your bag is empty.");
      } catch (error) {
        announce(container, error.message);
        try { swapBody(container, await fetchBody(), focusWas); } catch { setBusy(liveBody, false); }
      }
    });
    return queue;
  };

  const lineQty = (line) => Number.parseInt(line?.querySelector('[data-cart-control="qty"]')?.value, 10) || 0;
  const timers = new WeakMap();

  document.addEventListener("click", (event) => {
    const button = event.target.closest('[data-cart-body] [data-cart-control]');
    if (!button || button.matches('[data-cart-control="qty"]')) return;
    const body = button.closest("[data-cart-body]");
    const line = button.closest("[data-cart-line-key]");
    if (!line) return;
    event.preventDefault();
    const control = button.dataset.cartControl;
    const now = lineQty(line);
    const quantity = control === "remove" ? 0 : Math.max(0, now + (control === "inc" ? 1 : -1));
    const input = line.querySelector('[data-cart-control="qty"]');
    if (input) input.value = String(quantity); // instant feedback while Shopify answers
    change(body, line.dataset.cartLineKey, quantity);
  });

  const commitTyped = (input) => {
    window.clearTimeout(timers.get(input));
    const line = input.closest("[data-cart-line-key]");
    const quantity = Number.parseInt(input.value, 10);
    if (!line || !Number.isFinite(quantity) || quantity < 0) return;
    if (String(quantity) === input.dataset.cartOriginalQuantity) return;
    input.dataset.cartOriginalQuantity = String(quantity);
    change(input.closest("[data-cart-body]"), line.dataset.cartLineKey, quantity);
  };

  document.addEventListener("input", (event) => {
    const input = event.target.closest?.('[data-cart-body] [data-cart-control="qty"]');
    if (!input) return;
    window.clearTimeout(timers.get(input));
    timers.set(input, window.setTimeout(() => commitTyped(input), TYPE_DELAY));
  });
  document.addEventListener("change", (event) => {
    const input = event.target.closest?.('[data-cart-body] [data-cart-control="qty"]');
    if (input) commitTyped(input);
  });

  // Enter in a qty box or "Update bag" updates in place; Checkout still submits to Shopify.
  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.("[data-cart-body]");
    if (!form || event.submitter?.name === "checkout") return;
    event.preventDefault();
    form.querySelectorAll('[data-cart-control="qty"]').forEach(commitTyped);
  });

  // ---- empty bag: prefetch the builder on intent (hover, focus, press) ------------------
  // Works for the /cart page and the drawer, and for bodies swapped in later (delegated).
  const prefetchedPages = new Set();
  const prefetchPage = (event) => {
    const link = event.target.closest?.("a[data-prefetch-on-intent]");
    if (!link || prefetchedPages.has(link.href)) return;
    prefetchedPages.add(link.href);
    const hint = document.createElement("link");
    hint.rel = "prefetch";
    hint.href = link.href;
    document.head.append(hint);
  };
  ["pointerover", "focusin", "pointerdown", "touchstart"].forEach((type) =>
    document.addEventListener(type, prefetchPage, { passive: true, capture: true }));

  // ---- drawer ---------------------------------------------------------------------------
  if (drawer) {
    drawer.addEventListener("site-panel:open", () => refresh(drawerContent));
    const bag = document.querySelector('[data-panel-open="cart-drawer"]');
    ["pointerenter", "focus", "touchstart"].forEach((type) =>
      bag?.addEventListener(type, prefetch, { passive: true }));
  }

  window.saysoCartDrawer = {
    open(trigger = null) {
      if (!drawer || !window.saysoPanels) return false;
      if (window.saysoPanels.isOpen(drawer.id)) { refresh(drawerContent); return true; }
      return window.saysoPanels.open(drawer.id, trigger || document.querySelector('[data-panel-open="cart-drawer"]'));
    },
    refresh: () => refresh(drawerContent),
    setCount: setBagCount,
  };
})();
